import React from 'react';
import { SequenceStep } from '../../types/campaign';

interface SequenceTimelineProps {
  steps: SequenceStep[];
}

export const SequenceTimeline: React.FC<SequenceTimelineProps> = ({ steps }) => {
  const calculateTotalDuration = () => {
    let totalHours = 0;
    let totalDays = 0;

    steps.forEach((step) => {
      if (step.delay_hours) {
        totalHours += step.delay_hours;
      }
      if (step.delay_days) {
        totalDays += step.delay_days;
      }
    });

    // Convert hours to days if >= 24
    if (totalHours >= 24) {
      totalDays += Math.floor(totalHours / 24);
      totalHours = totalHours % 24;
    }

    const parts: string[] = [];
    if (totalDays > 0) {
      parts.push(`${totalDays} day${totalDays !== 1 ? 's' : ''}`);
    }
    if (totalHours > 0) {
      parts.push(`${totalHours} hour${totalHours !== 1 ? 's' : ''}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'Immediate';
  };

  const formatStepDelay = (step: SequenceStep, index: number) => {
    if (index === 0) {
      return 'Immediate';
    }

    const parts: string[] = [];
    if (step.delay_days && step.delay_days > 0) {
      parts.push(`${step.delay_days} day${step.delay_days !== 1 ? 's' : ''}`);
    }
    if (step.delay_hours && step.delay_hours > 0) {
      parts.push(`${step.delay_hours} hour${step.delay_hours !== 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      return 'Immediate';
    }

    return `+${parts.join(', ')}`;
  };

  if (steps.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No steps in this sequence</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Timeline</h3>
          <span className="text-sm text-gray-500">
            Total duration: <span className="font-medium">{calculateTotalDuration()}</span>
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-6">
          {steps.map((step, index) => (
            <div key={step.id} className="relative flex items-start">
              {/* Timeline dot */}
              <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 border-2 border-white">
                <span className="text-xs font-semibold text-indigo-800">{index + 1}</span>
              </div>

              {/* Step content */}
              <div className="ml-4 flex-1">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{step.name}</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {step.step_type.charAt(0).toUpperCase() + step.step_type.slice(1)}
                        {step.template && ` • ${step.template.name}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium text-gray-900">
                        {formatStepDelay(step, index)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

